import React, { useState, useEffect } from 'react';
import { api, API_BASE_URL } from '../services/api';
import './PortfolioMasterPage.css'; // Reuse master page styles

const initialIssuer = {
  issuer_id: '',
  company_name: '',
  short_name: '',
  long_name: '',
  registration_number: '',
  tin_number: '',
  vat_number: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  country: '',
  postal_code: '',
  phone_number: '',
  email: '',
  website: '',
  kyc_status: 'Pending',
  risk_category: 'Low',
  sanctions_check: 'Passed',
  credit_limit: '',
  bank_name: '',
  bank_branch: '',
  contact_person: ''
};

export default function IssuerMasterPage() {
  const [form, setForm] = useState(initialIssuer);
  const [issuers, setIssuers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchIssuers();
  }, []);

  async function fetchIssuers() {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/issuer-master');
      const issuersData = Array.isArray(res.data) ? res.data : [];
      console.log('Fetched issuers:', issuersData);
      setIssuers(issuersData);
    } catch (e) {
      console.error('Error fetching issuers:', e);
      setError('Failed to load issuers: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      if (editingId) {
        // Use the primary key 'id' for update, not issuer_id
        const updateId = editingId;
        console.log('Updating issuer with ID:', updateId);
        console.log('Form data:', form);
        
        // Don't send issuer_id in update - it's a unique identifier that shouldn't change
        const updateData = { ...form };
        // Keep issuer_id for reference but backend will ignore it in UPDATE
        
        const response = await api.put(`/issuer-master/${updateId}`, updateData);
        console.log('Update response:', response.data);
        setSuccess('Issuer updated successfully');
      } else {
        await api.post('/issuer-master', form);
        setSuccess('Issuer created successfully');
      }
      setForm(initialIssuer);
      setEditingId(null);
      await fetchIssuers();
    } catch (e) {
      console.error('Error saving issuer:', e);
      const errorMessage = e.response?.data?.error || e.response?.data?.details || e.message || 'Failed to save issuer';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(issuer) {
    // Use the primary key 'id' for editing, fallback to issuer_id if id doesn't exist
    const editId = issuer.id || issuer.issuer_id;
    
    console.log('Editing issuer:', issuer);
    console.log('Using edit ID:', editId);
    
    setForm({
      issuer_id: issuer.issuer_id || '',
      company_name: issuer.company_name || '',
      short_name: issuer.short_name || '',
      long_name: issuer.long_name || '',
      registration_number: issuer.registration_number || '',
      tin_number: issuer.tin_number || '',
      vat_number: issuer.vat_number || '',
      address_line1: issuer.address_line1 || '',
      address_line2: issuer.address_line2 || '',
      city: issuer.city || '',
      state: issuer.state || '',
      country: issuer.country || '',
      postal_code: issuer.postal_code || '',
      phone_number: issuer.phone_number || '',
      email: issuer.email || '',
      website: issuer.website || '',
      kyc_status: issuer.kyc_status || 'Pending',
      risk_category: issuer.risk_category || 'Low',
      sanctions_check: issuer.sanctions_check || 'Passed',
      credit_limit: issuer.credit_limit ? String(issuer.credit_limit) : '',
      bank_name: issuer.bank_name || '',
      bank_branch: issuer.bank_branch || '',
      contact_person: issuer.contact_person || ''
    });
    setEditingId(editId);
    setError('');
    setSuccess('');
    
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this issuer?')) return;
    setLoading(true);
    setError('');
    try {
      await api.delete(`/issuer-master/${id}`);
      setSuccess('Issuer deleted successfully');
      await fetchIssuers();
    } catch (e) {
      console.error('Error deleting issuer:', e);
      setError('Failed to delete issuer: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }

  function handleCancel() {
    setForm(initialIssuer);
    setEditingId(null);
    setError('');
    setSuccess('');
  }

  return (
    <div className="page-container ui-page">
      <h2 style={{ color: '#000', textAlign: 'center', marginBottom: '1.5rem', fontWeight: 'bold' }}>Issuer Master</h2>

      <form className="form-section compact-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label>Issuer ID</label>
            <input
              name="issuer_id"
              value={form.issuer_id}
              onChange={handleChange}
              required
              disabled={!!editingId}
            />
          </div>
          <div className="form-group">
            <label>Company Name</label>
            <input
              name="company_name"
              value={form.company_name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Short Name</label>
            <input
              name="short_name"
              value={form.short_name}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Long Name</label>
            <input
              name="long_name"
              value={form.long_name}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Registration Number</label>
            <input name="registration_number" value={form.registration_number} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>TIN Number</label>
            <input name="tin_number" value={form.tin_number} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>VAT Number</label>
            <input name="vat_number" value={form.vat_number} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Credit Limit</label>
            <input type="number" step="0.01" name="credit_limit" value={form.credit_limit} onChange={handleChange} />
          </div>
        </div>

        <fieldset className="form-row">
          <legend style={{ color: '#000', fontWeight: 600 }}>Address</legend>
          <div className="form-group">
            <label>Address Line 1</label>
            <input name="address_line1" value={form.address_line1} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Address Line 2</label>
            <input name="address_line2" value={form.address_line2} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>City</label>
            <input name="city" value={form.city} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>State / Province</label>
            <input name="state" value={form.state} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Country</label>
            <input name="country" value={form.country} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Postal Code</label>
            <input name="postal_code" value={form.postal_code} onChange={handleChange} />
          </div>
        </fieldset>

        <fieldset className="form-row">
          <legend style={{ color: '#000', fontWeight: 600 }}>Contact & Bank Details</legend>
          <div className="form-group">
            <label>Phone Number</label>
            <input name="phone_number" value={form.phone_number} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" name="email" value={form.email} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Website</label>
            <input name="website" value={form.website} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Bank</label>
            <input name="bank_name" value={form.bank_name} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Branch</label>
            <input name="bank_branch" value={form.bank_branch} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Contact Person</label>
            <input name="contact_person" value={form.contact_person} onChange={handleChange} />
          </div>
        </fieldset>

        <div className="form-row">
          <div className="form-group">
            <label>KYC Status</label>
            <select name="kyc_status" value={form.kyc_status} onChange={handleChange}>
              <option value="Pending">Pending</option>
              <option value="Verified">Verified</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
          <div className="form-group">
            <label>Risk Category</label>
            <select name="risk_category" value={form.risk_category} onChange={handleChange}>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </div>
          <div className="form-group">
            <label>Sanctions Check</label>
            <select name="sanctions_check" value={form.sanctions_check} onChange={handleChange}>
              <option value="Passed">Passed</option>
              <option value="Failed">Failed</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <button type="submit" className="save-verify-button" disabled={loading}>
            {editingId ? 'Update Issuer' : 'Create Issuer'}
          </button>
          {editingId && (
            <button type="button" className="action-button" onClick={handleCancel}>
              Cancel
            </button>
          )}
        </div>
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
      </form>

      <div className="transactions-section">
        <h3 style={{ color: '#000', fontWeight: 600 }}>Existing Issuers</h3>
        {loading && <div>Loading...</div>}
        <table className="transactions-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Company</th>
              <th>Short Name</th>
              <th>City</th>
              <th>Country</th>
              <th>Bank</th>
              <th>Contact Person</th>
              <th>Risk</th>
              <th>KYC</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {issuers.map(issuer => (
              <tr key={issuer.issuer_id || issuer.id}>
                <td>{issuer.issuer_id || issuer.id}</td>
                <td>{issuer.company_name}</td>
                <td>{issuer.short_name}</td>
                <td>{issuer.city}</td>
                <td>{issuer.country}</td>
                <td>{issuer.bank_name}</td>
                <td>{issuer.contact_person}</td>
                <td>{issuer.risk_category}</td>
                <td>{issuer.kyc_status}</td>
                <td>
                  <button
                    className="action-button edit-button"
                    onClick={() => handleEdit(issuer)}
                  >
                    Edit
                  </button>
                  <button
                    className="action-button delete-button"
                    onClick={() => handleDelete(issuer.id || issuer.issuer_id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {issuers.length === 0 && !loading && (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center' }}>
                  No issuers found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
